import type { Questionnaire } from '../types';

export const seedQuestionnaire: Questionnaire = {
  id: 'bexar-civil-spring-2026',
  jurisdiction: 'Bexar County Civil',
  term: 'Spring 2026',
  title: 'Bexar County Civil Jury Qualification Spring 2026',
  intro: 'Required for all summoned jurors before reporting date.',
  createdAt: '2026-01-15T00:00:00.000Z',
  updatedAt: '2026-05-05T00:00:00.000Z',
  sections: [
    {
      id: 'sec-juror-info',
      title: 'Juror Information',
      description: 'Confirm your contact and identifying information.',
      iconKey: 'person',
      questions: [
        {
          id: 'q-full-name',
          type: '2',
          inputKind: 'text',
          text: 'Full legal name',
          required: true,
          confidential: false,
          helperText: 'First Middle Last',
        },
        {
          id: 'q-dob',
          type: '8',
          inputKind: 'date',
          text: 'Date of birth',
          required: true,
          confidential: true,
        },
        {
          id: 'q-juror-number',
          type: '2',
          inputKind: 'text',
          text: 'Juror number (from your summons)',
          required: true,
          confidential: false,
          helperText: '8-digit number on the upper right of your summons.',
        },
        {
          id: 'q-address',
          type: '12',
          inputKind: 'address',
          text: 'Residential address',
          required: true,
          confidential: false,
        },
        {
          id: 'q-phone',
          type: '5',
          inputKind: 'tel',
          text: 'Daytime phone',
          required: true,
          confidential: false,
        },
        {
          id: 'q-email',
          type: '4',
          inputKind: 'email',
          text: 'Email address',
          required: false,
          confidential: false,
        },
      ],
    },
    {
      id: 'sec-eligibility',
      title: 'Eligibility',
      iconKey: 'eligibility',
      questions: [
        {
          id: 'q-citizen',
          type: '1',
          inputKind: 'boolean',
          text: 'Are you a U.S. citizen?',
          required: true,
          confidential: false,
          followUps: {
            no: [
              {
                id: 'q-citizen-no-detail',
                type: '3',
                inputKind: 'long-text',
                text: 'Please describe your status',
                required: true,
                confidential: true,
              },
            ],
          },
        },
        {
          id: 'q-age',
          type: '1',
          inputKind: 'boolean',
          text: 'Are you at least 18 years of age?',
          required: true,
          confidential: false,
        },
        {
          id: 'q-resident',
          type: '1',
          inputKind: 'boolean',
          text: 'Do you reside in Hamilton County?',
          required: true,
          confidential: false,
          followUps: {
            no: [
              {
                id: 'q-current-address',
                type: '12',
                inputKind: 'address',
                text: 'Current address of residence',
                required: true,
                confidential: false,
              },
              {
                id: 'q-moved-date',
                type: '8',
                inputKind: 'date',
                text: 'Date you moved from Hamilton County',
                required: true,
                confidential: false,
              },
            ],
          },
        },
        {
          id: 'q-felony',
          type: '1',
          inputKind: 'boolean',
          text: 'Have you been convicted of a felony?',
          required: true,
          confidential: true,
          followUps: {
            yes: [
              {
                id: 'q-felony-detail',
                type: '3',
                inputKind: 'long-text',
                text: 'Please describe the conviction',
                required: true,
                confidential: true,
              },
              {
                id: 'q-felony-doc',
                // No legacy QuestionnaireCustomQuestionType backs a file upload - this is
                // demo-only seed data (mock mode), not a real, save-able type id.
                type: '-1',
                inputKind: 'file',
                text: 'Documentation of rights restoration',
                required: false,
                confidential: true,
              },
            ],
          },
        },
      ],
    },
    {
      id: 'sec-hardship',
      title: 'Hardship & Availability',
      iconKey: 'calendar',
      questions: [
        {
          id: 'q-hardship',
          type: '1',
          inputKind: 'boolean',
          text: 'Would jury service cause you significant financial or personal hardship?',
          required: true,
          confidential: false,
          followUps: {
            yes: [
              {
                id: 'q-hardship-type',
                type: '7',
                inputKind: 'options-single',
                text: 'What is the primary nature of the hardship?',
                required: true,
                confidential: false,
                options: ['Financial', 'Medical', 'Caregiving', 'Travel', 'Other'],
              },
              {
                id: 'q-hardship-detail',
                type: '3',
                inputKind: 'long-text',
                text: 'Please explain the hardship in detail',
                required: true,
                confidential: false,
              },
              {
                id: 'q-hardship-doc',
                type: '-1',
                inputKind: 'file',
                text: 'Supporting documentation (employer letter, medical note, itinerary)',
                required: false,
                confidential: false,
              },
            ],
          },
        },
        {
          id: 'q-avail-first',
          type: '8',
          inputKind: 'date',
          text: 'First date you would be unavailable',
          required: false,
          confidential: false,
        },
        {
          id: 'q-avail-last',
          type: '8',
          inputKind: 'date',
          text: 'Last date you would be unavailable',
          required: false,
          confidential: false,
        },
      ],
    },
    {
      id: 'sec-prior',
      title: 'Prior Service & Background',
      iconKey: 'history',
      questions: [
        {
          id: 'q-served',
          type: '1',
          inputKind: 'boolean',
          text: 'Have you served on a jury in the past 24 months?',
          required: true,
          confidential: false,
          followUps: {
            yes: [
              {
                id: 'q-served-date',
                type: '8',
                inputKind: 'date',
                text: 'Approximate date of last service',
                required: false,
                confidential: false,
              },
              {
                id: 'q-served-type',
                type: '7',
                inputKind: 'options-single',
                text: 'Type of case',
                required: true,
                confidential: false,
                options: ['Civil', 'Criminal', 'Grand jury', "Don't recall"],
              },
            ],
          },
        },
        {
          id: 'q-occupation',
          type: '10',
          inputKind: 'options-multi',
          text: 'Which of the following describes your occupation?',
          required: false,
          confidential: false,
          options: [
            'Law enforcement',
            'Legal',
            'Medical',
            'Education',
            'Government',
            'Other',
          ],
        },
        {
          id: 'q-family-legal',
          type: '1',
          inputKind: 'boolean',
          text: 'Have you, a family member, or close friend worked in law or law enforcement?',
          required: true,
          confidential: false,
          followUps: {
            yes: [
              {
                id: 'q-family-legal-detail',
                type: '3',
                inputKind: 'long-text',
                text: 'Briefly describe the relationship and role',
                required: false,
                confidential: false,
              },
            ],
          },
        },
        {
          id: 'q-party',
          type: '1',
          inputKind: 'boolean',
          text: 'Have you ever been a party to a lawsuit?',
          required: true,
          confidential: false,
          followUps: {
            yes: [
              {
                id: 'q-party-detail',
                type: '3',
                inputKind: 'long-text',
                text: 'Please describe',
                required: true,
                confidential: true,
              },
            ],
          },
        },
      ],
    },
    {
      id: 'sec-bias',
      title: 'Bias & Impartiality',
      iconKey: 'scales',
      questions: [
        {
          id: 'q-views',
          type: '1',
          inputKind: 'boolean',
          text: 'Do you hold any views that would prevent you from being impartial?',
          required: true,
          confidential: false,
          followUps: {
            yes: [
              {
                id: 'q-views-detail',
                type: '3',
                inputKind: 'long-text',
                text: 'Please explain',
                required: true,
                confidential: true,
              },
            ],
          },
        },
        {
          id: 'q-news',
          type: '1',
          inputKind: 'boolean',
          text: 'Have you read or heard about this case in the news or on social media?',
          required: true,
          confidential: false,
          followUps: {
            yes: [
              {
                id: 'q-news-source',
                type: '7',
                inputKind: 'options-single',
                text: 'How did you learn about it?',
                required: true,
                confidential: false,
                options: [
                  'Television',
                  'Newspaper',
                  'Online article',
                  'Social media',
                  'Conversation',
                  'Other',
                ],
              },
              {
                id: 'q-news-opinion',
                type: '1',
                inputKind: 'boolean',
                text: "Have you formed any opinion about the case based on what you've heard?",
                required: true,
                confidential: true,
                followUps: {
                  yes: [
                    {
                      id: 'q-news-opinion-detail',
                      type: '3',
                      inputKind: 'long-text',
                      text: 'What is your current opinion?',
                      required: true,
                      confidential: true,
                    },
                  ],
                },
              },
            ],
          },
        },
      ],
    },
  ],
};
